/* eslint-disable react/prop-types */
/* eslint-disable import/no-unresolved */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import React, {
  useState, useEffect, useContext, useCallback, useMemo,
} from 'react';
import moment from 'moment-timezone';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Search, Users, TrendingUp, TrendingDown, Plus,
} from 'lucide-react';
import { motion } from 'motion/react';
import { Navbar, Sidebar } from '../../components';
import CreateTeamModal from './CreateTeamModal';
import './teamEngagement.css';
import TeamCard from './TeamCard';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';
import useDebounce from '../../hooks/useDebounce';
import { getMemberRoleLevel } from '../../utils/permissions';
import { accountTypeLevel } from '../../constants/accountTypes';
import { getDatesInRange } from '../../utils/time';
import DateRangePicker from '../../components/dateRangePicker';

const softShadow = 'shadow-[0_1px_2px_rgba(14,30,64,0.04)]';

const ENGAGEMENT_BUCKETS = [
  { id: 'all', label: 'All Teams' },
  { id: 'high', label: 'High (70%+)' },
  { id: 'mid', label: 'Medium (40-69%)' },
  { id: 'low', label: 'At Risk (<40%)' },
];

function TeamEngagement() {
  const today = new Date();
  const endOfLast7Days = today;
  const startOfLast7Days = new Date(today);
  startOfLast7Days.setDate(today.getDate() - 6);

  if (startOfLast7Days.getMonth() !== endOfLast7Days.getMonth()) {
    startOfLast7Days.setDate(1);
  }
  const { displayNotification } = useContext(NotificationContext);
  const { activeWorkspace, member } = useContext(UserContext);
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [cards, setCards] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [bucket, setBucket] = useState('all');
  const { debounce } = useDebounce();
  const [date, setDate] = useState([startOfLast7Days, endOfLast7Days]);

  // silent: refresh card data without flashing the loader over existing cards
  const fetchTeams = async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    const [startDate, endDate] = date;
    const dates = getDatesInRange(startDate, endDate);
    const startOfDay = moment(dates[0]).startOf('day').format('YYYY-MM-DD HH:mm:ss');
    const endOfDay = moment(dates.at(-1)).endOf('day').format('YYYY-MM-DD HH:mm:ss');
    const { success, data: { teams } } = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.createTeam(activeWorkspace.id),
      timeout: 60000,
      params: {
        start: startOfDay,
        end: endOfDay,
        searchQuery,
      },
    });
    if (!success) {
      displayNotification({
        message: 'There was an issue fetching team data in this workspace. Please try again',
        severity: 'error',
      });
      return;
    }

    setCards(teams.map(({ id, name, teamMates }) => ({
      id,
      name,
      percentage: Math.round(teamMates.reduce((score, mate) => {
        const { totalActiveSeconds, totalTimeSeconds } = mate.member.dailyActivity.reduce((totals, activity) => {
          // eslint-disable-next-line no-param-reassign
          totals.totalActiveSeconds += activity.activeSeconds || 0;
          // eslint-disable-next-line no-param-reassign
          totals.totalTimeSeconds += activity.totalSeconds || 0;
          return totals;
        }, { totalActiveSeconds: 0, totalTimeSeconds: 0 });

        const matePercentage = Math.round((totalActiveSeconds * 100) / totalTimeSeconds || 0);
        return score + matePercentage;
      }, 0) / (teamMates.length || 1)),

      managers: teamMates
        .filter((mate) => mate.role === 'Manager')
        .map((manager) => ({
          id: manager.memberId,
          name: manager.member && manager.member.user
            ? `${manager.member.user.firstName} ${manager.member.user.lastName}`
            : 'Unknown Name',
        })),
      members: teamMates
        .filter((mate) => mate.role === 'Member')
        .map((mate) => ({
          id: mate.memberId,
          name: mate.member && mate.member.user
            ? `${mate.member.user.firstName} ${mate.member.user.lastName}`
            : 'Unknown Name',
        })),
      webcamViolations: teamMates
        .some(({ member: m }) => m && m.webcamViolations && m.webcamViolations.length > 0),
      locationViolations: teamMates
        .some(({ member: m }) => m && m.locationViolations && m.locationViolations.length > 0),
    })));

    setIsLoading(false);
  };

  useEffect(() => {
    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, date]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetSearch = useCallback(debounce((query) => setSearchQuery(query), 500), []);

  const handleSearch = (e) => {
    setSearchInput(e.target.value);
    debouncedSetSearch(e.target.value);
  };

  const totalMembers = useMemo(
    () => cards.reduce((sum, c) => sum + (c.members?.length || 0), 0),
    [cards],
  );
  const avgEngagement = useMemo(() => {
    if (cards.length === 0) return 0;
    return Math.round(cards.reduce((a, c) => a + c.percentage, 0) / cards.length);
  }, [cards]);
  const atRisk = useMemo(
    () => cards.filter((c) => c.percentage < 40).length,
    [cards],
  );

  const filtered = useMemo(() => cards.filter((c) => {
    if (bucket === 'all') return true;
    if (bucket === 'high') return c.percentage >= 70;
    if (bucket === 'mid') return c.percentage >= 40 && c.percentage < 70;
    return c.percentage < 40;
  }), [cards, bucket]);

  const isWorkspaceAdmin = getMemberRoleLevel(member) >= accountTypeLevel['Workspace admin'];

  const kpis = [
    {
      label: 'Total Teams',
      value: cards.length,
      sub: `${totalMembers} members tracked`,
      icon: Users,
      accent: '#3d5a8a',
    },
    {
      label: 'Avg Engagement',
      value: `${avgEngagement}%`,
      sub: avgEngagement >= 70 ? 'Above target' : avgEngagement >= 50 ? 'On track' : 'Below target',
      icon: avgEngagement >= 50 ? TrendingUp : TrendingDown,
      accent: avgEngagement >= 70 ? '#6b9f5f' : '#F19235',
    },
    {
      label: 'Teams At Risk',
      value: atRisk,
      sub: atRisk === 0 ? 'All teams healthy' : 'Below 40% engagement',
      icon: TrendingDown,
      accent: atRisk === 0 ? '#6b9f5f' : '#b94f3c',
    },
  ];

  // Show the new team immediately (a fresh team has no activity yet), then
  // reconcile with the server in the background without showing the loader.
  const handleTeamCreated = (newTeam) => {
    if (newTeam && newTeam.id) {
      setCards((prev) => [...prev, {
        id: newTeam.id,
        name: newTeam.name,
        percentage: 0,
        managers: newTeam.managers,
        members: newTeam.members,
        webcamViolations: false,
        locationViolations: false,
      }]);
    }
    fetchTeams({ silent: true });
  };

  return (
    <div>
      {showCreateTeamModal && (
        <CreateTeamModal
          onTeamCreated={handleTeamCreated}
          closeModal={() => setShowCreateTeamModal(false)}
          workspaceId={activeWorkspace.id}
        />
      )}
      <Navbar />
      <Sidebar />
      <div className="screen-wrapper">
        <div className="screen-header">
          <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h3 className="team-header-manage-member">Team Engagement</h3>
              {/* <p className="text-sm text-ink-500 mt-1"> */}
              {/*  Assess team performance and engagement */}
              {/* </p> */}
            </div>
            <div className="flex flex-nowrap items-center gap-2 sm:gap-3 sm:justify-end w-full sm:w-auto">
              {isWorkspaceAdmin && (
                <button
                  type="button"
                  onClick={() => setShowCreateTeamModal(true)}
                  data-tour="create-team"
                  className="inline-flex items-center justify-center gap-1.5 px-3 sm:px-4 h-10 rounded-lg bg-brand-500 text-white text-sm font-semibold hover:bg-brand-700 transition shadow-[0_1px_2px_rgba(14,30,64,0.08)] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 whitespace-nowrap flex-shrink-0"
                >
                  <Plus size={16} />
                  Create Team
                </button>
              )}
              <div className="flex-1 sm:flex-none min-w-0">
                <DateRangePicker
                  value={date}
                  onChange={(_date) => setDate(_date)}
                  className="w-auto"
                />
              </div>
            </div>
          </div>

          {/* pb-5 keeps the prototype's space-y-5 (20px) rhythm between the
              filter row and the cards grid in .screen-body below */}
          <div className="px-0 pt-0 pb-5 space-y-5">
            {/* KPI strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-tour="team-kpis">
              {kpis.map((k) => {
                const Icon = k.icon;
                return (
                  <div
                    key={k.label}
                    className={`rounded-xl bg-white border border-surface-2 ${softShadow} p-4`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-ink-500 uppercase tracking-wide">
                        {k.label}
                      </span>
                      <Icon size={14} style={{ color: k.accent }} />
                    </div>
                    <p className="text-2xl font-bold text-ink-900 leading-none">
                      {k.value}
                    </p>
                    <p className="text-xs text-ink-500 mt-2">{k.sub}</p>
                  </div>
                );
              })}
            </div>

            {/* Bucket filters + search row */}
            <div className="flex items-center gap-3 flex-wrap" data-tour="team-buckets">
              <div className="flex items-center gap-1.5 flex-wrap">
                {ENGAGEMENT_BUCKETS.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setBucket(b.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                      bucket === b.id
                        ? 'bg-brand-500 text-white'
                        : 'bg-white border border-surface-2 text-ink-500 hover:bg-gray-50'
                    }`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
              <div className="relative flex-1 sm:flex-none sm:w-64 sm:ml-auto">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500 pointer-events-none"
                />
                <input
                  type="text"
                  value={searchInput}
                  onChange={handleSearch}
                  placeholder="Search teams"
                  className="w-full pl-9 pr-3 py-2 border border-rule rounded-lg text-sm bg-white text-ink-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="screen-body">
          {!isLoading && filtered.length === 0 ? (
            <div className="rounded-xl bg-white border border-surface-2 p-10 text-center">
              <p className="text-sm font-medium text-ink-900">No teams match</p>
              <p className="text-xs text-ink-500 mt-1">
                Try a different search or filter.
              </p>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
            >
              {filtered.map(({
                id,
                name,
                percentage,
                managers,
                members,
                webcamViolations,
                locationViolations,
              }) => (
                <TeamCard
                  key={id}
                  teamId={id}
                  name={name}
                  onTeamDeleted={fetchTeams}
                  onTeamEdited={fetchTeams}
                  percentage={percentage}
                  managers={managers}
                  members={members}
                  dateRange={date}
                  workspaceId={activeWorkspace.id}
                  webcamViolations={webcamViolations}
                  locationViolations={locationViolations}
                />
              ))}
            </motion.div>
          )}
          {isLoading && (
            <div className="loader-container">
              <CircularProgress sx={{ color: 'inherit' }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TeamEngagement;
